import React, { FC, ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { Activity, AnyEntity } from "@mapmycustomers/shared/types/entity";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import { getPreviewEntityUniqueElementId } from "@mapmycustomers/shared/util/browser";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  DrillDownModal,
  DrillDownModalProps,
} from "@app/scene/dashboard/components/cards/common/DrillDownModal";
import {
  exportActivitiesLoggedCardDrillDownData,
  fetchActivitiesLoggedCardDrillDownData,
} from "@app/scene/dashboard/store/cards/activitiesLogged/actions";
import {
  getActivitiesLoggedDrillDownCustomDateRange,
  getActivitiesLoggedDrillDownTotalFilteredRecords,
  getActivitiesLoggedDrillDownViewState,
  isActivitiesLoggedDrillDownLoading,
} from "@app/scene/dashboard/store/cards/activitiesLogged/selectors";
import { DrillDownListFetcherPayload } from "@app/scene/dashboard/types/DrillDownListFetcher";
import formatDrillDownDateRange from "@app/scene/dashboard/utils/formatDrillDownDateRange";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import ActivitiesLoggedCardConfiguration from "@app/types/dashboard/cards/activitiesLogged/ActivitiesLoggedCardConfiguration";
import Scope from "@app/types/dashboard/Scope";

import messages from "../../messages";

interface Props extends Pick<DrillDownModalProps, "onEdit" | "onHide"> {
  configuration: ActivitiesLoggedCardConfiguration;
  customDateRange?: DateRange;
  onExportDrillDownData: typeof exportActivitiesLoggedCardDrillDownData;
  onFetchDrillDownData: (payload: DrillDownListFetcherPayload<Activity>) => void;
  onShowEntityView: typeof showEntityView;
  scope: Scope;
  totalFilteredRecords: number;
  viewState: ViewState;
}

const DrillDown: FC<Props> = ({
  configuration,
  customDateRange,
  onEdit,
  onExportDrillDownData,
  onFetchDrillDownData,
  onHide,
  onShowEntityView,
  scope,
  totalFilteredRecords,
  viewState,
}) => {
  const intl = useIntl();

  const handleExport = useCallback(() => {
    onExportDrillDownData({ configuration, scope });
  }, [onExportDrillDownData, configuration, scope]);

  const handleActivityTypeView = useCallback(
    (entity: AnyEntity, element: HTMLElement) => {
      onShowEntityView({
        elementId: getPreviewEntityUniqueElementId(element),
        entityId: entity.id,
        entityType: entity.entity as EntityType.ACTIVITY,
      });
    },
    [onShowEntityView]
  );

  const handleRecordView = useCallback(
    (entity: AnyEntity) => {
      onShowEntityView({
        entityId: entity.id,
        entityType: entity.entity as EntityType.COMPANY | EntityType.DEAL | EntityType.PERSON,
        zIndex: 1001, // to override the drill down modal
      });
    },
    [onShowEntityView]
  );

  return (
    <DrillDownModal
      entityType={EntityType.ACTIVITY}
      onActivityTypeView={handleActivityTypeView}
      onEdit={onEdit}
      onExportDrillDownData={handleExport}
      onFetchDrillDownData={onFetchDrillDownData}
      onHide={onHide}
      onRecordView={handleRecordView}
      subtitle={formatDrillDownDateRange(
        customDateRange ??
          getDatesRange(configuration.dateRange.range, configuration.dateRange.subRange)
      )}
      title={
        configuration.customTitle?.trim() ||
        intl.formatMessage(messages.cardTitle, {
          date: (text: ReactNode[]) => text,
          dateRange: configuration.dateRange.range,
          dateSubRange: configuration.dateRange.subRange,
        })
      }
      totalFilteredRecords={totalFilteredRecords}
      viewState={viewState}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  customDateRange: getActivitiesLoggedDrillDownCustomDateRange(state),
  loading: isActivitiesLoggedDrillDownLoading(state),
  totalFilteredRecords: getActivitiesLoggedDrillDownTotalFilteredRecords(state),
  viewState: getActivitiesLoggedDrillDownViewState(state),
});

const mapDispatchToProps = {
  onExportDrillDownData: exportActivitiesLoggedCardDrillDownData,
  onFetchDrillDownData: fetchActivitiesLoggedCardDrillDownData.request,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(DrillDown);
